import { useMemo, useState } from 'react';
import { ShieldCheck } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAction, useApi } from '../lib/useApi';
import { Empty, ErrorBox, Loading, Notice, PageHeader, StatusBadge, dayKey, dayLabel, fmtDate, fmtTime, plural } from '../components/ui';
import type { Child } from './DashboardPage';

interface Session {
  id: string;
  title: string;
  track: string;
  age_band: string;
  starts_at: string;
  duration_minutes: number;
  capacity: number;
  min_children: number;
  booked: number;
  status: string;
  tutor_name: string;
}
interface Booking {
  id: string;
  session_id: string;
  child_name: string;
  title: string;
  starts_at: string;
  session_status: string;
}

export function LiveCirclesPage() {
  const { user } = useAuth();
  const isParent = user?.role === 'parent';
  const sessions = useApi<{ sessions: Session[] }>('/live-circles');
  const bookings = useApi<{ bookings: Booking[] }>('/live-circles/bookings');
  const family = useApi<{ children: Child[] }>(isParent ? '/family' : null);
  const [pick, setPick] = useState<Record<string, string>>({});
  const [shownDays, setShownDays] = useState(4);
  const act = useAction();

  // A tutor who opens four weeks at once used to produce one flat wall of
  // near-identical cards, all called the same thing, with the date buried in a
  // grey line. Grouped by day, a parent reads it the way they read a diary.
  const days = useMemo(() => {
    const byDay = new Map<string, Session[]>();
    for (const s of sessions.data?.sessions ?? []) {
      const k = dayKey(s.starts_at);
      (byDay.get(k) ?? byDay.set(k, []).get(k)!).push(s);
    }
    return [...byDay.entries()];
  }, [sessions.data]);

  const join = async (sessionId: string) => {
    const r = await act.run(() => api<{ joinUrl: string }>(`/live-circles/${sessionId}/join`, { method: 'POST', body: {} }));
    if (r) window.open(r.joinUrl, '_blank', 'noopener');
  };

  return (
    <>
      <PageHeader title="Live Circles" termKey="page.live" lead="Small group lessons with vetted tutors." />
      <div className="alert safety with-icon">
        <ShieldCheck size={18} strokeWidth={1.75} aria-hidden="true" />
        <span>
          <strong>Always:</strong> at least 2 children · recorded for safeguarding · parents can join and watch any time · tutors are identity-, reference- and
          background-checked and safeguarding-trained · no chat or private messages.
        </span>
      </div>
      <Notice text={act.notice} />
      <ErrorBox error={act.error ?? sessions.error} />

      <h2>Your bookings</h2>
      {bookings.loading && <Loading />}
      {bookings.data?.bookings.length === 0 && <Empty>No bookings yet.</Empty>}
      <div className="grid grid-2">
        {bookings.data?.bookings.map((b) => (
          <div key={b.id} className="card spread">
            <div>
              <strong className="yo">{b.title}</strong>
              <div className="small muted">
                <span className="yo">{b.child_name}</span> · {fmtDate(b.starts_at)} · <StatusBadge status={b.session_status} />
              </div>
            </div>
            <div className="row">
              <button className="btn small" onClick={() => join(b.session_id)} disabled={b.session_status !== 'live'} title={b.session_status !== 'live' ? 'Opens when the tutor starts the circle' : undefined}>
                {isParent ? 'Join / watch' : 'Join'}
              </button>
              {isParent && b.session_status === 'scheduled' && (
                <button className="btn small ghost" onClick={async () => { await act.run(() => api(`/live-circles/bookings/${b.id}`, { method: 'DELETE' }), 'Booking cancelled.'); bookings.reload(); sessions.reload(); }}>
                  Cancel
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      <h2 style={{ marginTop: 24 }}>Upcoming circles</h2>
      {sessions.loading && <Loading />}
      {sessions.data && sessions.data.sessions.length === 0 && <Empty>No circles are scheduled yet. We’ll add them as tutors open their weeks.</Empty>}
      {days.slice(0, shownDays).map(([key, daySessions]) => (
        <section key={key} className="stack" style={{ marginTop: 18 }}>
          <h3 className="day-heading">
            {dayLabel(daySessions[0]!.starts_at)}{' '}
            <span className="muted small">
              {daySessions.length} {plural(daySessions.length, 'circle')}
            </span>
          </h3>
          <div className="grid grid-2">
            {daySessions.map((s) => {
              const eligible = (family.data?.children ?? []).filter((c) => c.age_band === s.age_band && c.active);
              return (
                <div key={s.id} className="card stack">
                  <div className="spread">
                    <h4 className="yo" style={{ margin: 0 }}>{s.title}</h4>
                    <span className="badge">ages {s.age_band}</span>
                  </div>
                  <p className="small muted" style={{ margin: 0 }}>
                    <strong>{fmtTime(s.starts_at)}</strong> · {s.duration_minutes} min · with <span className="yo">{s.tutor_name}</span>
                  </p>
                  <p className="small">
                    {s.booked}/{s.capacity} booked {s.booked < s.min_children && <span className="muted">(runs once {s.min_children} children are booked)</span>}
                  </p>
                  {isParent &&
                    (eligible.length ? (
                      <div className="row">
                        <select aria-label="Child" value={pick[s.id] ?? ''} onChange={(e) => setPick({ ...pick, [s.id]: e.target.value })}>
                          <option value="">Choose child…</option>
                          {eligible.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.display_name}
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn small"
                          disabled={!pick[s.id] || act.busy}
                          onClick={async () => {
                            await act.run(() => api(`/live-circles/${s.id}/book`, { body: { childProfileId: pick[s.id] } }), 'Booked! You’ll be able to join and watch.');
                            bookings.reload();
                            sessions.reload();
                          }}
                        >
                          Book
                        </button>
                      </div>
                    ) : (
                      <p className="small muted">No child in the {s.age_band} age band.</p>
                    ))}
                </div>
              );
            })}
          </div>
        </section>
      ))}
      {days.length > shownDays && (
        <div style={{ marginTop: 18 }}>
          <button className="btn secondary" onClick={() => setShownDays((n) => n + 4)}>
            Show more days ({days.length - shownDays} more)
          </button>
        </div>
      )}
    </>
  );
}
